import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { apiError, fmtD, fmtDT } from "@/lib/platform";
import { downloadEvidence } from "@/lib/rights";
import { BATCH_STYLES, HOLD_STYLES, Pill } from "@/lib/retention";
import { Field, Pick } from "@/components/vendors/NewVendorDialog";

const th = "px-4 py-3 font-semibold";
const Head = ({ cols }) => <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">{cols.map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>;
const Box = ({ children, testId }) => <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto"><table className="w-full text-sm" data-testid={testId}>{children}</table></div>;

export const DispositionQueue = ({ batches }) => {
  const nav = useNavigate();
  return (
    <Box testId="batch-table">
      <Head cols={["Batch", "Rule", "Cut-off", "Estimated / disposed", "Due", "Source", "Status"]} />
      <tbody>
        {batches.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-slate-500">No disposition batches.</td></tr>}
        {batches.map((b) => (
          <tr key={b.id} onClick={() => nav(`/retention/batches/${b.id}`)} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer" data-testid={`batch-row-${b.id}`}>
            <td className="px-4 py-3 font-mono text-xs text-slate-500">{b.batch_id}</td>
            <td className="px-4 py-3"><div className="font-medium text-[#003366]">{b.rule_name}</div><div className="text-[11px] text-slate-500">{b.system_name} · {b.storage_name}</div></td>
            <td className="px-4 py-3 text-xs font-mono">{fmtD(b.cutoff_date)}<div className="text-[10px] text-slate-400 font-sans">{b.trigger_label}</div></td>
            <td className="px-4 py-3 text-xs font-mono">{b.estimated_records ?? "—"} / {b.records_disposed ?? "—"}</td>
            <td className={`px-4 py-3 text-xs font-mono ${b.is_overdue ? "text-red-600 font-semibold" : "text-slate-600"}`}>{fmtD(b.due_date)}</td>
            <td className="px-4 py-3 text-[11px] text-slate-500">{b.source.toLowerCase()}</td>
            <td className="px-4 py-3"><div className="flex gap-1 flex-wrap">
              <Pill cls={BATCH_STYLES[b.status]} testId={`batch-status-${b.id}`}>{b.status_label}</Pill>
              {b.blocked_by.length > 0 && <Pill cls="bg-amber-50 text-amber-800 border-amber-200"><Lock className="w-3 h-3 mr-1" />{b.blocked_by.map((x) => x.hold_ref).join(", ")}</Pill>}
            </div></td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

export const HoldsList = ({ holds }) => {
  const nav = useNavigate();
  return (
    <Box testId="hold-table">
      <Head cols={["Hold", "Type", "Matter", "Scope", "Period", "Owner", "Status"]} />
      <tbody>
        {holds.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-slate-500">No holds.</td></tr>}
        {holds.map((h) => (
          <tr key={h.id} onClick={() => nav(`/retention/holds/${h.id}`)} className="border-b border-slate-50 hover:bg-slate-50/80 cursor-pointer" data-testid={`hold-row-${h.id}`}>
            <td className="px-4 py-3"><div className="font-medium text-[#003366]">{h.name}</div><div className="text-[11px] font-mono text-slate-500">{h.hold_id}</div></td>
            <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${HOLD_STYLES[h.hold_type]}`}>{h.type_label}</span></td>
            <td className="px-4 py-3 text-xs font-mono text-slate-600">{h.matter_reference || "—"}</td>
            <td className="px-4 py-3 text-xs text-slate-700">{h.scopes.map((s) => s.label).join(", ") || <span className="text-amber-700">No scope yet</span>}</td>
            <td className="px-4 py-3 text-xs font-mono">{fmtD(h.start_date)} → {h.end_date ? fmtD(h.end_date) : "open"}</td>
            <td className="px-4 py-3 text-xs">{h.owner}</td>
            <td className="px-4 py-3"><Pill cls={h.is_effective ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-500 border-slate-200"} testId={`hold-status-${h.id}`}>
              {h.is_effective ? h.status_label : h.status === "RELEASED" ? "Released" : "Not in effect"}</Pill></td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

export const CertificatesList = ({ certs }) => (
  <Box testId="certificate-table">
    <Head cols={["Certificate", "Batch / rule", "System / storage", "Records", "Issued", "SHA-256 (text)", "Files"]} />
    <tbody>
      {certs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-slate-500">No deletion certificates yet.</td></tr>}
      {certs.map((c) => (
        <tr key={c.id} className="border-b border-slate-50" data-testid={`certificate-row-${c.id}`}>
          <td className="px-4 py-3 font-mono text-xs font-semibold text-[#003366]">{c.certificate_number}</td>
          <td className="px-4 py-3 text-xs"><span className="font-mono">{c.batch_ref}</span><div className="text-[11px] text-slate-500">{c.rule_ref} · {c.rule_name}</div></td>
          <td className="px-4 py-3 text-xs">{c.system_name}<div className="text-[11px] text-slate-500">{c.storage_name}</div></td>
          <td className="px-4 py-3 text-xs font-mono">{c.records_disposed}</td>
          <td className="px-4 py-3 text-xs">{fmtDT(c.issued_at)}<div className="text-[11px] text-slate-500">{c.issued_by}</div></td>
          <td className="px-4 py-3 text-[10px] font-mono text-slate-500" title={c.text_sha256}>{c.text_sha256.slice(0, 20)}…</td>
          <td className="px-4 py-3"><div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => downloadEvidence(c.pdf_evidence_id)} data-testid={`certificate-pdf-${c.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => downloadEvidence(c.text_evidence_id)} data-testid={`certificate-txt-${c.id}`}>.txt</Button>
          </div></td>
        </tr>
      ))}
    </tbody>
  </Box>
);

export const HoldDialog = ({ open, onOpenChange, meta, onSaved }) => {
  const [f, setF] = useState({ hold_type: "LEGAL", name: "", reason: "", matter_reference: "", start_date: "", end_date: "" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const nav = useNavigate();
  const save = async () => {
    try {
      const { data } = await api.post("/retention/holds", { ...f, start_date: f.start_date || null, end_date: f.end_date || null });
      toast.success(`${data.hold_id} created. Add its scope next.`);
      onOpenChange(false); onSaved?.(); nav(`/retention/holds/${data.id}`);
    } catch (e) { toast.error(apiError(e, "Could not create hold")); }
  };
  if (!meta) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="hold-dialog">
        <DialogHeader><DialogTitle>New hold</DialogTitle><DialogDescription>An active hold blocks any disposition it overlaps until the hold is released through approval.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hold type"><Pick value={f.hold_type} onChange={set("hold_type")} options={Object.entries(meta.hold_types)} testId="hold-type" /></Field>
          <Field label="Matter reference"><Input value={f.matter_reference} onChange={set("matter_reference")} data-testid="hold-matter" /></Field>
          <Field label="Name" className="col-span-2"><Input value={f.name} onChange={set("name")} data-testid="hold-name" /></Field>
          <Field label="Reason" className="col-span-2"><Textarea rows={2} value={f.reason} onChange={set("reason")} data-testid="hold-reason" /></Field>
          <Field label="Start (defaults to today)"><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="hold-start" /></Field>
          <Field label="End (optional)"><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="hold-end" /></Field>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!f.name.trim() || !f.reason.trim()} onClick={save} className="bg-[#003366]" data-testid="hold-save">Create hold</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
